import React, { useState } from 'react'
import { styled } from 'styled-components'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Navigation } from 'swiper'
import MuxPlayer from '@mux/mux-player-react'
import { FiLock } from 'react-icons/fi'

import useIsMobile from '@/util/useIsMobile'
import { addSizeToUrl } from '@/util/imgHelpers'
import { Paywall } from '@/components/Paywall'
import { useCheckout } from 'src/hooks/useCheckout'
import { useABGroup } from 'src/hooks/useABGroup'

// Modal for enlarged view
const Modal = ({ src, type, onClose }) => {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {type === 'video' ? (
          <MuxPlayer
            style={{ width: '100%', height: 'auto' }}
            playbackId={src}
            streamType="on-demand"
            controls
            autoplay
          />
        ) : (
          <img src={src} alt="Enlarged content" />
        )}
      </ModalContent>
    </ModalOverlay>
  )
}

export const PostSwiper = ({ uploads, post, locked = false }) => {
  const isMobile = useIsMobile()
  const [modalOpen, setModalOpen] = useState(false)
  const [activeMedia, setActiveMedia] = useState(null)
  const { handleCheckout } = useCheckout()
  const group = useABGroup('discover-test-group', ['a', 'b'])
  const buttonGroup = useABGroup('button-test-group', ['a', 'b'])
  const openModal = (src, type) => {
    setActiveMedia({ src, type })
    setModalOpen(true)
  }
  const closeModal = () => setModalOpen(false)

  const modules = [Pagination, Navigation]

  const paidImagesCount = uploads.filter((upload) => !upload.free && !upload.video).length
  const paidVideosCount = uploads.filter((upload) => !upload.free && upload.video).length
  const totalPaidCount = paidImagesCount + paidVideosCount

  const unlockLabelParts = []
  if (paidImagesCount > 0) {
    unlockLabelParts.push(`${paidImagesCount} image${paidImagesCount > 1 ? 's' : ''}`)
  }
  if (paidVideosCount > 0) {
    unlockLabelParts.push(`${paidVideosCount} video${paidVideosCount > 1 ? 's' : ''}`)
  }
  const unlockLabel = unlockLabelParts.join(' and ')

  const finalLabel = totalPaidCount === 0
    ? `Unlock for $${post.price}`
    : isMobile
      ? `Unlock ${totalPaidCount} item${totalPaidCount > 1 ? 's' : ''} for $${post.price}`
      : `Unlock ${unlockLabel} for $${post.price}`

  const getButtonText = () => {
    if (buttonGroup === 'b') {
      return `Get Full Access - $${post.price}`
    }
    return `Unlock for $${post.price}`
  }

  const onPaywallClick = post => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'paywall_click', {
        method: 'paywall_button',
        ab_group: group || 'unknown',
        button_group: buttonGroup || 'unknown',
        post_id: post.id,
        value: post.price
      });
    }

    handleCheckout({
      id: post.id,
      title: post.title,
      url: post.id,
      price: post.price * 100,
      endpoint: 'creators/checkout'
    });
  }

  return (
    <Container className="mx-auto">
      <SwiperWrapper>
        <Swiper
          pagination={{ clickable: true }}
          navigation
          modules={modules}
          spaceBetween={10}
          slidesPerView={1}
        >
          {/* a/b testing variant */}
          {group === 'b' && locked && (
            <SwiperSlide>
              <SlideWrapper>
                <BlurredOverlay bg={addSizeToUrl(post.coverImg, post.coverImgTitle)} />
                <PaywallButton
                  onClick={() => onPaywallClick(post)}
                  $buttonGroup={buttonGroup}
                >
                  <FiLock className="lock-icon" />
                  {getButtonText()}
                </PaywallButton>
                <PaywallSubtext>{finalLabel}</PaywallSubtext>
              </SlideWrapper>
            </SwiperSlide>
          )}
          <SwiperSlide>
            <SlideWrapper>
              <StyledImage
                src={addSizeToUrl(post.coverImg, post.coverImgTitle)}
                alt={post.title || 'Post Media'}
                onError={(e) => {
                  if (post.coverImg && e.currentTarget.src !== post.coverImg) {
                    e.currentTarget.src = post.coverImg
                  }
                }}
                onClick={() => openModal(post.coverImg, 'image')}
              />
            </SlideWrapper>
          </SwiperSlide>

          {uploads.map((upload, index) => (
            <SwiperSlide key={upload.id || index}>
              <SlideWrapper>
                {locked && !upload.free ? (
                  <>
                    <BlurredOverlay bg={addSizeToUrl(post.coverImg, post.coverImgTitle)} />
                    <PaywallButton
                      onClick={() => onPaywallClick(post)}
                      $buttonGroup={buttonGroup}
                    >
                      <FiLock className="lock-icon" />
                      {getButtonText()}
                    </PaywallButton>
                    <PaywallSubtext>{finalLabel}</PaywallSubtext>
                  </>
                ) : upload.video ? (
                  <StyledMuxPlayer
                    playbackId={upload.muxId}
                    streamType="on-demand"
                    loop
                    muted
                    autoplay
                    playsInline
                    draggable={false}
                    onClick={() => openModal(upload.muxId, 'video')}
                  />
                ) : (
                  <StyledImage
                    src={upload.imageUrl}
                    alt={upload.title || 'Post Media'}
                    onClick={() => openModal(upload.imageUrl, 'image')}
                  />
                )}
              </SlideWrapper>
            </SwiperSlide>
          ))}
        </Swiper>
      </SwiperWrapper>

      {modalOpen && activeMedia && (
        <Modal
          src={activeMedia.src}
          type={activeMedia.type}
          onClose={closeModal}
        />
      )}
    </Container>
  )
}

const Container = styled.div``

const SlideWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  min-height: 650px;
`

const SwiperWrapper = styled.div`
  width: 100%;
  max-height: calc(100vh - 120px);
  overflow: hidden;
`

const StyledImage = styled.img`
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  cursor: pointer;
  transition: opacity 0.5s;
  user-drag: none;
`

const StyledMuxPlayer = styled(MuxPlayer)`
  width: 100%;
  max-height: 90vh;
  object-fit: contain;
`

const BlurredOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${(props) => props.bg});
  background-size: cover;
  background-position: center;
  filter: blur(5px);
  z-index: 10;
`

const PaywallButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${props => props.$buttonGroup === 'b' ? '#546433' : '#fff'};
  color: ${props => props.$buttonGroup === 'b' ? '#fff' : '#000'};
  border: none;
  cursor: pointer;
  font-weight: bold;
  z-index: 20;
  max-width: 340px;
  min-width: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 16px;
  text-align: center;
  white-space: nowrap;
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);

  transition: all 0.25s ease;

  .lock-icon {
    font-size: 20px;
  }

  &:hover {
    background: ${props => props.$buttonGroup === 'b' ? '#4a5a2e' : '#f5f5f5'};
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
  }

  @media (max-width: 640px) {
    font-size: 14px;
    max-width: 100%;
    min-width: auto;
    padding: 10px 14px;
  }
`

const PaywallSubtext = styled.div`
  position: absolute;
  top: calc(50% + 40px);
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 14px;
  text-align: center;
  z-index: 20;
  max-width: 90%;
  line-height: 1.4;
  font-weight: 600;

  @media (max-width: 640px) {
    font-size: 13px;
    top: calc(50% + 35px);
    max-width: 95%;
  }
`

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 999;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ModalContent = styled.div`
  max-width: 90%;
  max-height: 90%;
  img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
  }
`